import { api, readTotalCount } from '../api/client';
import { API_COLLECTIONS_URL } from '../utils';
import { normalizeId } from '../utils/ids';
import { withElementChoicesField } from '../utils/elementRefusal';

/**
 * COLLECTION LINKS — "collection A shares with collection B" (keepr-api
 * docs/SCHEMA.md § Collection links; keepr-web
 * docs/plans/cross-collection-collaboration.md).
 *
 * A link is a `grants` row on A whose principal is B: B's collection-level
 * members read A inside the link's shape, read-only, with A the source of
 * truth. Two ways a link comes to exist, because both sides have to agree:
 *
 *   - someone who manages BOTH collections makes it directly (`createLink`);
 *   - otherwise B's manager asks (`requestLink`) and A's manager answers
 *     (`approveLinkRequest`, which may narrow the shape, or `declineLinkRequest`).
 *
 * Either side ends it (`removeLink`: A's managers revoke, B's remove).
 *
 * Every mutation here is session-only and applies immediately — like the rest
 * of the Sharing tab, nothing waits for the settings form's Save button.
 *
 * This module is the ONE client for the link endpoints. It is arranged by the
 * API's own PRs so later ones extend it in place: L1/L2 (the rows and the
 * requests) are below; W2's reads through a link (lookups, "Linked from",
 * Copy into B) and W4's merged lists add their calls beside these rather than
 * in a second module.
 *
 * Wire shapes (the API's serializers, utils/collectionLinkUtils.js):
 *
 *   link    { _id, sourceId, targetId, source, target, role: 'read', card_id,
 *             query, allowCopies, allowKeys, createdAt, updatedAt,
 *             createdBy?, updatedBy? }  — `source`/`target` are
 *             { _id, name, icon, color, status } or null (deleted)
 *   request { _id, sourceId, targetId, source, target, card_id, query, note,
 *             status, requestedBy, createdAt, decidedAt, decisionNote, linkId,
 *             decidedBy?, requesterReach? }
 */

const linksUrl = (collectionId, suffix = '') =>
	`${API_COLLECTIONS_URL}/${collectionId}/links${suffix ? `/${suffix}` : ''}`;

const requestsUrl = (collectionId, suffix = '') =>
	`${API_COLLECTIONS_URL}/${collectionId}/link-requests${suffix ? `/${suffix}` : ''}`;

/** The API's defaults and caps, for copy that names them (env-adjustable on the server). */
export const LINK_LIMITS = Object.freeze({ outgoing: 50, incoming: 50, noteMax: 500 });

/**
 * What a link (or a request) covers, in one reading:
 *   'whole' — every record of A (no card, no filter)
 *   'card'  — one card of A and its descendants
 *   'query' — the records a read-only KQL filter matches
 * A row never carries both a card and a query (the API refuses it).
 */
export function linkShape(row) {
	// A purge took what it was (keepr-api KPR-659): no card and no filter here
	// does not mean the whole collection.
	if (row?.shapeRemoved) return { kind: 'removed', cardId: null, query: null };
	const cardId = normalizeId(row?.card_id) || null;
	const query = typeof row?.query === 'string' && row.query.trim() ? row.query : null;
	if (cardId) return { kind: 'card', cardId, query: null };
	if (query) return { kind: 'query', cardId: null, query };
	return { kind: 'whole', cardId: null, query: null };
}

/** The shape as a payload: at most one of card_id / query, nothing for a whole link. */
function shapePayload({ card_id: cardId, query } = {}) {
	const card = normalizeId(cardId);
	if (card) return { card_id: card };
	const text = typeof query === 'string' ? query.trim() : '';
	return text ? { query: text } : {};
}

const list = value => (Array.isArray(value) ? value : []);

/**
 * `GET /api/collections/{id}/links`, in the words this client uses. The API
 * names requests by the direction the LINK would run, which reads backwards
 * from where you stand, so this renames them once, here:
 *
 *   outgoing       what {id} shares (managers only — null otherwise)
 *   incoming       what {id} reads from other collections (every member)
 *   requestsToAnswer  others asking {id} to share with them (the API's
 *                  `requests.outgoing`; managers only — null otherwise)
 *   requestsSent   {id}'s own pending asks (the API's `requests.incoming`;
 *                  managers only — null otherwise)
 *   manages        whether the API answered as to a manager
 */
export function normalizeLinksView(data) {
	const requests = data?.requests && typeof data.requests === 'object' ? data.requests : null;
	const manages = Array.isArray(data?.outgoing) || requests !== null;
	return {
		outgoing: manages ? list(data?.outgoing) : null,
		incoming: list(data?.incoming),
		requestsToAnswer: requests ? list(requests.outgoing) : null,
		requestsSent: requests ? list(requests.incoming) : null,
		manages,
	};
}

export async function getCollectionLinks(collectionId) {
	const { data } = await api.get(linksUrl(collectionId));
	return normalizeLinksView(data);
}

/**
 * What `collectionId` USED to share and read — `GET /api/collections/{id}/
 * links/history` (managers): every ended link and every decided request it
 * stood on either side of, newest first, read off the change log, which
 * keeps them for good. → { rows, total }
 *
 *   entry { _id, kind: 'link' | 'request', event: 'revoke' | 'remove' |
 *           'approve' | 'decline' | 'withdraw', direction: 'outgoing' |
 *           'incoming', sourceId, targetId, source, target, label, linkId,
 *           requestId, card_id, card, query, allowCopies?, allowKeys?,
 *           startedAt, startedBy, endedAt, endedBy, endedBySide: 'this' |
 *           'other' | 'system', note?, decisionNote? }
 *
 * The receiving side is never told who on the sharing side acted (`endedBy`
 * null, `endedBySide: 'other'`), and is given a card's name only where the
 * link itself named it — `card_id` alone otherwise, as the live list.
 */
export async function getLinkHistory(collectionId, { limit, skip } = {}) {
	const params = {};
	if (limit !== undefined) params.limit = limit;
	if (skip !== undefined) params.skip = skip;
	const { data, headers } = await api.get(linksUrl(collectionId, 'history'), { params });
	const rows = list(data);
	return { rows, total: readTotalCount(headers, rows.length) };
}

/**
 * Make (or re-state) a link from `sourceId` to `targetId` — manage on BOTH is
 * needed for a new one (403 `link_needs_request` otherwise). The POST is a
 * COMPLETE statement, idempotent per (target, card): an absent query clears
 * one and absent switches return to their defaults (copies off, keys on), so
 * both switches are always sent.
 */
export async function createLink(sourceId, {
	targetId, card_id: cardId, query, allowCopies = false, allowKeys = true, elementChoices = null,
} = {}) {
	const payload = {
		targetId: normalizeId(targetId),
		...shapePayload({ card_id: cardId, query }),
		allowCopies: allowCopies === true,
		allowKeys: allowKeys !== false,
	};
	// The person's answer to an ambiguous element name (utils/elementRefusal.js).
	const { data } = await api.post(linksUrl(sourceId), withElementChoicesField(payload, payload.query ? elementChoices : null));
	return data;
}

/**
 * Change a link from A's side — a PARTIAL statement: only the fields given go
 * up. `query: null` removes the filter (A-only managers may narrow, never
 * widen: 403 `link_widen_needs_request`). The target and the card are the
 * row's identity and never change here.
 */
export async function updateLink(sourceId, linkId, { query, allowCopies, allowKeys, elementChoices = null } = {}) {
	const payload = {};
	if (query !== undefined) payload.query = typeof query === 'string' && query.trim() ? query.trim() : null;
	if (allowCopies !== undefined) payload.allowCopies = allowCopies === true;
	if (allowKeys !== undefined) payload.allowKeys = allowKeys === true;
	const { data } = await api.put(linksUrl(sourceId, linkId), withElementChoicesField(payload, payload.query ? elementChoices : null));
	return data;
}

/**
 * End a link from either side: `collectionId` the source is A's REVOKE, the
 * target is B's REMOVE. → { ok, side: 'source' | 'target' }
 */
export async function removeLink(collectionId, linkId) {
	const { data } = await api.delete(linksUrl(collectionId, linkId));
	return data;
}

/**
 * B's manager asks A to share. `targetId` is the asking collection (B), the
 * path's {id}; the switches are A's to set at approval, so none go up here.
 */
export async function requestLink(targetId, { sourceId, card_id: cardId, query, note } = {}) {
	const payload = { sourceId: normalizeId(sourceId), ...shapePayload({ card_id: cardId, query }) };
	const text = typeof note === 'string' ? note.trim() : '';
	if (text) payload.note = text;
	const { data } = await api.post(requestsUrl(targetId), payload);
	return data;
}

/** B takes its own pending request back. `targetId` is B. */
export async function withdrawLinkRequest(targetId, requestId) {
	const { data } = await api.post(requestsUrl(targetId, `${requestId}/withdraw`), {});
	return data;
}

/**
 * A's manager says yes. With no `shape` the link is written as asked; with
 * one it must be NARROWER (400 `link_widens_request`). The switches always go
 * up — they are A's decision and this is where A makes it.
 * → { request, link }
 */
export async function approveLinkRequest(sourceId, requestId, {
	shape = null, allowCopies = false, allowKeys = true, elementChoices = null,
} = {}) {
	const payload = { allowCopies: allowCopies === true, allowKeys: allowKeys !== false };
	if (shape) {
		const narrowed = shapePayload(shape);
		// An explicit whole shape is `card_id: null` (the API reads "none named"
		// as "as asked", so a whole approval of a whole request sends nothing).
		Object.assign(payload, Object.keys(narrowed).length ? narrowed : { card_id: null });
	}
	// The person's answer to an ambiguous element name in a filter the
	// approval writes (utils/elementRefusal.js); the API lifts it off any
	// route's body before validation (keepr-api utils/elementChoices.js).
	const { data } = await api.post(requestsUrl(sourceId, `${requestId}/approve`), withElementChoicesField(payload, elementChoices));
	return data;
}

/** A's manager says no, optionally saying why (kept in keepr, never mailed). */
export async function declineLinkRequest(sourceId, requestId, { note } = {}) {
	const text = typeof note === 'string' ? note.trim() : '';
	const { data } = await api.post(requestsUrl(sourceId, `${requestId}/decline`), text ? { note: text } : {});
	return data;
}

// ------------------------------------------------------------------ reading through a link (W2 / W4)

/**
 * The cards a live link lends this collection — `GET /api/collections/{B}/
 * cards?includeLinked=1` appends them after B's own, each carrying
 * `borrowedFrom: { collection: { collection_id, name, icon, color } }` and
 * `readOnly: true` (keepr-api § Merged lists). Only those are returned here.
 * An API without merged lists answers B's own cards alone, which is none.
 */
export async function getBorrowedCards(collectionId) {
	const { data } = await api.get(`${API_COLLECTIONS_URL}/${collectionId}/cards`, { params: { includeLinked: 1 } });
	return list(data).filter(card => card && card.borrowedFrom && typeof card.borrowedFrom === 'object');
}

/**
 * The way out of a failed keep-copy (409 `link_copy_failed`): turn the
 * link's copies off, so the next end is a tombstone. The refusal names the
 * exact call (`fix: { method: 'PUT', path, payload }`); this is it, for a
 * manager of the source.
 */
export async function turnLinkCopiesOff(sourceId, linkId) {
	return updateLink(sourceId, linkId, { allowCopies: false });
}
